import { initializeApp } from 'firebase/app';
import { getFirestore, doc, setDoc, getDoc, deleteDoc, collection, query, where, getDocs, writeBatch, runTransaction, connectFirestoreEmulator, collectionGroup } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, signInAnonymously, updateProfile, EmailAuthProvider, reauthenticateWithCredential, reauthenticateWithPopup, updatePassword, deleteUser, fetchSignInMethodsForEmail, setPersistence, browserSessionPersistence, browserLocalPersistence, inMemoryPersistence, connectAuthEmulator } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';

// Firebase 구성 정보
const firebaseConfig = {
  apiKey: "AIzaSyBZtupWsi2TauHkFkPJlXQjAvd30SkbIQA",
  authDomain: "whatquizaiplatform.firebaseapp.com",
  projectId: "whatquizaiplatform",
  storageBucket: "whatquizaiplatform.firebasestorage.app",
  messagingSenderId: "590620435886",
  appId: "1:590620435886:web:28cf4e39332d0c5539543f",
  measurementId: "G-TNPQ7YLR51"
};

// 환경변수 확인
if (process.env.NODE_ENV === 'development') {
  // Firebase 구성 로드 완료
}

// Firebase 앱 초기화 및 Firestore 인스턴스 생성
let db = null;
let auth = null;
let app = null;
let storage = null;
let analytics = null;
let functions = null;

try {
  app = initializeApp(firebaseConfig);
  
  if (app) {
    try {
      auth = getAuth(app);
      // 기본 지속성은 로컬 스토리지로 설정 (브라우저를 닫아도 로그인 유지)
      try { await setPersistence(auth, browserLocalPersistence); } catch (_) {}
    } catch (authError) {
      // Firebase Auth 초기화 오류 무시
    }
    
    try {
      db = getFirestore(app);
    } catch (dbError) {
      // Firestore 초기화 오류 무시
    }
    
    try {
      storage = getStorage(app);
    } catch (storageError) {
      // Firebase Storage 초기화 오류 무시
    }
    
    try {
      functions = getFunctions(app);
    } catch (functionsError) {
      // Firebase Functions 초기화 오류 무시
    }
  }
} catch (error) {
  // Firebase 초기화 오류 무시
}

// Analytics 지연 로딩 함수
export const loadAnalytics = async () => {
  try {
    // 동적 import로 Analytics만 필요할 때 로드
    const { getAnalytics, logEvent, isSupported } = await import('firebase/analytics');
    
    const analyticsSupported = await isSupported();
    if (analyticsSupported && app && !analytics) {
      analytics = getAnalytics(app);
      return analytics;
    }
  } catch (error) {
    // Analytics 로드 실패 무시
  }
  return null;
};

// Analytics 이벤트 로깅 함수 (지연 로딩)
export const logAnalyticsEvent = async (eventName, eventParams = {}) => {
  if (!eventName) return false;
  
  try {
    // Analytics가 로드되지 않았다면 지연 로딩
    if (!analytics) {
      analytics = await loadAnalytics();
    }
    
    if (analytics) {
      const { logEvent } = await import('firebase/analytics');
      
      const enhancedParams = {
        ...eventParams,
        send_to: 'G-TNPQ7YLR51',
        timestamp: new Date().toISOString(),
        platform: 'web'
      };
      
      logEvent(analytics, eventName, enhancedParams);
      return true;
    }
  } catch (error) {
    // Analytics 이벤트 로깅 실패 무시
  }
  return false;
};

// Firebase 연결 상태 확인 함수
export const checkFirebaseConnection = async () => {
  const status = {
    app: !!app,
    auth: !!auth,
    firestore: !!db,
    storage: !!storage,
    functions: !!functions,
    analytics: !!analytics,
    overall: false
  };
  
  try {
    // Firestore 연결 테스트
    if (db) {
      // 임시 문서 참조로 연결 테스트 (실제로 읽지는 않음)
      const testDoc = doc(db, 'test', 'connection');
      status.firestoreConnected = true;
    }
    
    // 전체 상태 판단 (최소 app, auth, firestore가 있어야 함)
    status.overall = status.app && status.auth && status.firestore;
    
  } catch (error) {
    status.firestoreConnected = false;
  }
  
  return status;
};

// Firestore 안전한 쓰기 함수 (재시도 로직 포함)
export const safeFirestoreWrite = async (docRef, data, options = {}) => {
  const maxRetries = 3;
  let lastError = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      if (options.merge) {
        await setDoc(docRef, data, { merge: true });
      } else {
        await setDoc(docRef, data);
      }
      
      return { success: true, error: null };
    } catch (error) {
      lastError = error;
      
      if (attempt < maxRetries) {
        // 지수 백오프: 1초, 2초, 4초 대기
        const delay = Math.pow(2, attempt - 1) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  return { success: false, error: lastError };
};

// Firestore 안전한 읽기 함수 (재시도 로직 포함)
export const safeFirestoreRead = async (docRef) => {
  const maxRetries = 3;
  let lastError = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const docSnap = await getDoc(docRef);
      
      return { success: true, doc: docSnap, error: null };
    } catch (error) {
      lastError = error;
      
      if (attempt < maxRetries) {
        // 지수 백오프: 1초, 2초, 4초 대기
        const delay = Math.pow(2, attempt - 1) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  return { success: false, doc: null, error: lastError };
};

// users 컬렉션에 사용자 정보 저장
export const saveUserToFirestore = async (userId, userData) => {
  try {
    if (!db) {
      return { success: false, error: "Firestore가 초기화되지 않았습니다." };
    }
    
    // users 컬렉션의 userId 문서에 데이터 저장
    const userRef = doc(db, "users", userId);
    await setDoc(userRef, userData, { merge: true });
    return { success: true, error: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 인증 관련 함수들
// 회원가입 함수
export const registerWithEmailAndPassword = async (email, password, additionalData = {}) => {
  try {
    if (!auth) {
      return { user: null, error: "Firebase Auth가 초기화되지 않았습니다." };
    }
    
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // UID를 문서 ID로 사용
    const docId = user.uid;
    
    // 기존 사용자인지 확인 (Google과 동일한 방식)
    const existingUser = await getUserFromFirestore(docId);
    const isNewUser = !existingUser.success;
    
    // 사용자 정보 구성 (Google과 최대한 비슷하게)
    const userData = {
      uid: user.uid,
      email: email,
      displayName: '', // 이메일 회원가입은 빈 문자열로 시작
      photoURL: '', // 이메일 회원가입은 빈 문자열로 시작
      provider: 'email',
      emailVerified: user.emailVerified,
      phoneNumber: user.phoneNumber || '',
      lastLogin: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // 마케팅 동의 정보 추가
      agreeMarketing: additionalData.agreeMarketing || false,
      marketingConsentAt: additionalData.agreeMarketing ? new Date().toISOString() : null
    };
    
    // 신규 사용자인 경우 추가 필드 (Google과 동일)
    if (isNewUser) {
      userData.createdAt = new Date().toISOString();
      userData.isNewUser = true;
      userData.profileComplete = false; // 프로필 완성 여부
      userData.preferences = {
        language: 'ko',
        notifications: true,
        theme: 'light'
      };
    } else {
      userData.isNewUser = false;
    }
    
    // Firestore에 사용자 정보 저장
    const saveResult = await saveUserToFirestore(docId, userData);
    if (!saveResult.success) {
      // 저장 실패해도 회원가입은 성공으로 처리
    }
    
    // Analytics 이벤트 로깅
    await logAnalyticsEvent('sign_up', {
      method: 'email',
      user_id: user.uid,
      marketing_consent: additionalData.agreeMarketing || false
    });
    
    return { 
      user: user, 
      error: null, 
      isNewUser: isNewUser,
      userData: userData 
    };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// 로그인 함수
export const loginWithEmailAndPassword = async (email, password, remember = false) => {
  try {
    if (!auth) {
      return { user: null, error: "Firebase Auth가 초기화되지 않았습니다." };
    }
    
    // 이메일 로그인: remember 값과 상관없이 항상 로컬 스토리지 사용
    try {
      await setPersistence(auth, browserLocalPersistence);
    } catch (_) {}

    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // 이메일을 문서 ID로 사용 (특수문자 제거)
    const docId = email.replace(/[.#$[\]]/g, "_");
    
    // 마지막 로그인 시간 업데이트
    await saveUserToFirestore(docId, {
      lastLogin: new Date().toISOString()
    });
    
    return { user: userCredential.user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// 로그아웃 함수
export const logoutUser = async () => {
  try {
    if (!auth) {
      return { success: false, error: "Firebase Auth가 초기화되지 않았습니다." };
    }
    
    await signOut(auth);
    return { success: true, error: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 현재 사용자 확인
export const getCurrentUser = () => {
  if (!auth) return null;
  return auth.currentUser;
};

// Google 로그인 함수
export const signInWithGoogle = async (remember = true) => {
  try {
    if (!auth) {
      return { user: null, error: "Firebase Auth가 초기화되지 않았습니다." };
    }
    // 구글 로그인 지속성: remember 값과 상관없이 항상 로컬 스토리지 사용
    try { await setPersistence(auth, browserLocalPersistence); } catch (_) {}
    const provider = new GoogleAuthProvider();
    
    // 추가 권한 요청 (선택사항)
    provider.addScope('profile');
    provider.addScope('email');
    
    // Cross-Origin-Opener-Policy 관련 설정 개선
    provider.setCustomParameters({
      prompt: 'select_account',
      // Cross-Origin 정책 호환성을 위한 설정
      access_type: 'online',
      include_granted_scopes: 'true',
      // window.close 호출 방지를 위한 설정
      ux_mode: 'popup'
    });

    // Cross-Origin-Opener-Policy 오류 방지를 위한 안전한 popup 실행
    const userCredential = await signInWithPopup(auth, provider).catch((popupError) => {
      // Cross-Origin 관련 오류나 window.close 관련 오류인 경우 더 구체적인 처리
      if (popupError.message && (
          popupError.message.includes('Cross-Origin-Opener-Policy') ||
          popupError.message.includes('window.close') ||
          popupError.message.includes('window.closed')
        )) {
        throw new Error('popup-blocked-cors');
      }
      throw popupError;
    });
    const user = userCredential.user;
    
    // UID를 문서 ID로 사용 (더 안전하고 표준적인 방식)
    const docId = user.uid;
    
    // 기존 사용자 확인 (타임아웃 설정을 3초로 늘리고 더 안정적으로 처리)
    let isNewUser = true;
    try {
      const existingUserPromise = getUserFromFirestore(docId);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('timeout')), 3000)
      );
      
      const existingUser = await Promise.race([existingUserPromise, timeoutPromise]);
      if (existingUser.success && existingUser.data) {
        isNewUser = false;
      } else {
        // 데이터가 없으면 신규 사용자
        isNewUser = true;
      }
    } catch (error) {
      // 타임아웃이나 오류 발생 시 신규 사용자로 가정
      isNewUser = true;
    }
    
    // 사용자 정보 구성
    const userData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || '',
      photoURL: user.photoURL || '',
      provider: 'google',
      emailVerified: user.emailVerified,
      phoneNumber: user.phoneNumber || '',
      lastLogin: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      agreeMarketing: false,
      marketingConsentAt: null
    };
    
    // 신규 사용자인 경우 추가 필드
    if (isNewUser) {
      userData.createdAt = new Date().toISOString();
      userData.isNewUser = true;
      userData.profileComplete = false;
      userData.preferences = {
        language: 'ko',
        notifications: true,
        theme: 'light'
      };
    } else {
      userData.isNewUser = false;
    }
    
    // 백그라운드에서 Firestore 저장과 Analytics 로깅 처리
    const backgroundTasks = async () => {
      try {
        // Firestore에 사용자 정보 저장/업데이트
        await saveUserToFirestore(docId, userData);
        
        // Analytics 이벤트 로깅
        await logAnalyticsEvent(isNewUser ? 'sign_up' : 'login', {
          method: 'google',
          user_id: user.uid
        });
        
    
      } catch (error) {
        // 백그라운드 작업 실패 무시
      }
    };
    
    // 백그라운드 작업 시작
    backgroundTasks();
    
    return {
      user: user,
      error: null,
      isNewUser: isNewUser,
      userData: userData
    };
  } catch (error) {
    
    // 구체적인 오류 처리
    let errorMessage = error.message;
    if (error.code === 'auth/popup-closed-by-user') {
      errorMessage = 'popup-closed-by-user';
    } else if (error.code === 'auth/popup-blocked') {
      errorMessage = 'popup-blocked';
    } else if (error.code === 'auth/cancelled-popup-request') {
      errorMessage = 'popup-closed-by-user';
    } else if (error.code === 'auth/network-request-failed') {
      errorMessage = '네트워크 연결을 확인해주세요.';
    } else if (error.message === 'popup-blocked-cors' || 
               (error.message && error.message.includes('Cross-Origin-Opener-Policy'))) {
      errorMessage = 'Cross-Origin-Opener-Policy';
    }
    
    return { user: null, error: errorMessage, isNewUser: false };
  }
};

// Redirect 방식 Google 로그인 함수 (popup이 차단될 경우 대안)
export const signInWithGoogleRedirect = async (remember = true) => {
  try {
    if (!auth) {
      return { success: false, error: "Firebase Auth가 초기화되지 않았습니다." };
    }
    // 구글 리다이렉트 지속성: remember에 따라 분기
    try { await setPersistence(auth, remember ? browserLocalPersistence : inMemoryPersistence); } catch (_) {}
    
    const provider = new GoogleAuthProvider();
    
    // 추가 권한 요청
    provider.addScope('profile');
    provider.addScope('email');
    provider.setCustomParameters({
      prompt: 'select_account',
      // Cross-Origin 정책 호환성을 위한 설정
      access_type: 'online',
      include_granted_scopes: 'true',
      // window.close 호출 방지를 위한 설정
      ux_mode: 'redirect'
    });
    
    // redirect 방식으로 로그인 시작
    await signInWithRedirect(auth, provider);
    return { success: true, error: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Redirect 결과 처리 함수 (페이지 로드 시 호출)
export const handleGoogleRedirectResult = async () => {
  try {
    if (!auth) {
      return { user: null, error: "Firebase Auth가 초기화되지 않았습니다." };
    }
    
    const result = await getRedirectResult(auth);
    
    if (result) {
      const user = result.user;
      
      // UID를 문서 ID로 사용
      const docId = user.uid;
      
      // 기존 사용자 확인 (타임아웃 설정을 3초로 늘리고 더 안정적으로 처리)
      let isNewUser = true;
      try {
        const existingUserPromise = getUserFromFirestore(docId);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('timeout')), 3000)
        );
        
        const existingUser = await Promise.race([existingUserPromise, timeoutPromise]);
        if (existingUser.success && existingUser.data) {
          isNewUser = false;
        } else {
          // 데이터가 없으면 신규 사용자
          isNewUser = true;
        }
      } catch (error) {
        // 타임아웃이나 오류 발생 시 신규 사용자로 가정
        isNewUser = true;
      }
      
      // 사용자 정보 구성
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        provider: 'google',
        emailVerified: user.emailVerified,
        phoneNumber: user.phoneNumber || '',
        lastLogin: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        agreeMarketing: false,
        marketingConsentAt: null
      };
      
      // 신규 사용자인 경우 추가 필드
      if (isNewUser) {
        userData.createdAt = new Date().toISOString();
        userData.isNewUser = true;
        userData.profileComplete = false;
        userData.preferences = {
          language: 'ko',
          notifications: true,
          theme: 'light'
        };
      } else {
        userData.isNewUser = false;
      }
      
      // 백그라운드에서 Firestore 저장과 Analytics 로깅 처리
      const backgroundTasks = async () => {
        try {
          // Firestore에 사용자 정보 저장/업데이트
          await saveUserToFirestore(docId, userData);
          
          // Analytics 이벤트 로깅
          await logAnalyticsEvent(isNewUser ? 'sign_up' : 'login', {
            method: 'google_redirect',
            user_id: user.uid
          });
          
      
        } catch (error) {
          // 백그라운드 작업 실패 무시
        }
      };
      
      // 백그라운드 작업 시작
      backgroundTasks();
      
      return { 
        user: user, 
        error: null, 
        isNewUser: isNewUser,
        userData: userData 
      };
    }
    
    return { user: null, error: null, isNewUser: false };
  } catch (error) {
    return { user: null, error: error.message, isNewUser: false };
  }
};

// 통합 Google 로그인 함수 (popup 시도 후 실패 시 redirect로 전환)
export const signInWithGoogleAuto = async (remember = true) => {
  try {
    // 먼저 popup 방식만 시도하고, 실패/취소 시에도 리다이렉트로 전환하지 않음
    // => 로그인 화면을 그대로 유지하기 위해 폴백 리다이렉트를 제거
    const popupResult = await signInWithGoogle(remember);
    return popupResult;
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// 익명 로그인 함수
export const signInAnonymousUser = async () => {
  try {
    if (!auth) {
      return { user: null, error: "Firebase Auth가 초기화되지 않았습니다." };
    }
    
    const userCredential = await signInAnonymously(auth);
    
    // Analytics 이벤트 로깅
    await logAnalyticsEvent('login', {
      method: 'anonymous',
      user_id: userCredential.user.uid
    });
    
    return { user: userCredential.user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// 익명 사용자 데이터를 새 계정으로 이전하는 함수
export const migrateAnonymousUserData = async (anonymousUser, newUser) => {
  try {
    if (!db) {
      return { success: false, error: "Firestore가 초기화되지 않았습니다." };
    }

    const anonymousUid = anonymousUser.uid;
    const newUid = newUser.uid;
    
    const migratedCollections = [];
    const errors = [];

    // 1. teacher_test_group 컬렉션 데이터 이전
    try {
      const anonymousTeacherDocRef = doc(db, 'teacher_test_group', anonymousUid);
      const anonymousTeacherDoc = await getDoc(anonymousTeacherDocRef);
      
      if (anonymousTeacherDoc.exists()) {
        const anonymousData = anonymousTeacherDoc.data();
        
        // 새 UID로 문서 데이터 업데이트
        const newTeacherData = {
          ...anonymousData,
          uid: newUid,
          email: newUser.email,
          displayName: newUser.displayName || '',
          provider: newUser.providerData?.[0]?.providerId || 'email',
          updatedAt: new Date().toISOString(),
          migratedFrom: anonymousUid,
          migratedAt: new Date().toISOString()
        };
        
        // 새 UID로 문서 생성
        const newTeacherDocRef = doc(db, 'teacher_test_group', newUid);
        await setDoc(newTeacherDocRef, newTeacherData);
        
        // 기존 익명 문서 삭제
        await deleteDoc(anonymousTeacherDocRef);
        
        migratedCollections.push('teacher_test_group');
      }
    } catch (error) {
      errors.push(`teacher_test_group: ${error.message}`);
    }

    // 2. marble_drawing_member 컬렉션 데이터 이전 (random_picker에서 변경)
    try {
      const anonymousMarbleDocRef = doc(db, 'marble_drawing_member', anonymousUid);
      const anonymousMarbleDoc = await getDoc(anonymousMarbleDocRef);
      
      if (anonymousMarbleDoc.exists()) {
        const anonymousData = anonymousMarbleDoc.data();
        
        // 새 UID로 문서 데이터 업데이트
        const newMarbleData = {
          ...anonymousData,
          uid: newUid,
          email: newUser.email,
          displayName: newUser.displayName || '',
          provider: newUser.providerData?.[0]?.providerId || 'email',
          updatedAt: new Date().toISOString(),
          migratedFrom: anonymousUid,
          migratedAt: new Date().toISOString()
        };
        
        // 새 UID로 문서 생성
        const newMarbleDocRef = doc(db, 'marble_drawing_member', newUid);
        await setDoc(newMarbleDocRef, newMarbleData);
        
        // 기존 익명 문서 삭제
        await deleteDoc(anonymousMarbleDocRef);
        
        migratedCollections.push('marble_drawing_member');
      }
    } catch (error) {
      errors.push(`marble_drawing_member: ${error.message}`);
    }

    // 3. groups 컬렉션에서 createdBy 필드 업데이트
    try {
      const groupsQuery = query(collection(db, 'groups'), where('createdBy', '==', anonymousUid));
      const groupsSnapshot = await getDocs(groupsQuery);
      
      if (!groupsSnapshot.empty) {
        const batch = writeBatch(db);
        
        groupsSnapshot.docs.forEach((doc) => {
          batch.update(doc.ref, {
            createdBy: newUid,
            updatedAt: new Date().toISOString(),
            migratedFrom: anonymousUid,
            migratedAt: new Date().toISOString()
          });
        });
        
        await batch.commit();
        migratedCollections.push(`groups (${groupsSnapshot.docs.length} documents)`);
      }
    } catch (error) {
      errors.push(`groups (createdBy): ${error.message}`);
    }

    // 4. groups 컬렉션에서 students 배열 내 UID 업데이트
    try {
      const allGroupsSnapshot = await getDocs(collection(db, 'groups'));
      const batch = writeBatch(db);
      let updatedGroupsCount = 0;
      
      allGroupsSnapshot.docs.forEach((doc) => {
        const groupData = doc.data();
        if (groupData.students && Array.isArray(groupData.students)) {
          let hasUpdates = false;
          
          const updatedStudents = groupData.students.map(student => {
            if (student.uid === anonymousUid || student.id === anonymousUid) {
              hasUpdates = true;
              return {
                ...student,
                uid: newUid,
                id: newUid,
                email: newUser.email,
                displayName: newUser.displayName || student.displayName || '',
                migratedFrom: anonymousUid,
                migratedAt: new Date().toISOString()
              };
            }
            return student;
          });
          
          if (hasUpdates) {
            batch.update(doc.ref, { 
              students: updatedStudents,
              updatedAt: new Date().toISOString()
            });
            updatedGroupsCount++;
          }
        }
      });
      
      if (updatedGroupsCount > 0) {
        await batch.commit();
        migratedCollections.push(`groups (students - ${updatedGroupsCount} documents)`);
      }
    } catch (error) {
      errors.push(`groups (students): ${error.message}`);
    }

    // 5. role_draw_group 컬렉션에서 createdBy 필드 업데이트
    try {
      const roleGroupsQuery = query(collection(db, 'role_draw_group'), where('createdBy', '==', anonymousUid));
      const roleGroupsSnapshot = await getDocs(roleGroupsQuery);
      
      if (!roleGroupsSnapshot.empty) {
        const batch = writeBatch(db);
        
        roleGroupsSnapshot.docs.forEach((doc) => {
          batch.update(doc.ref, {
            createdBy: newUid,
            updatedAt: new Date().toISOString(),
            migratedFrom: anonymousUid,
            migratedAt: new Date().toISOString()
          });
        });
        
        await batch.commit();
        migratedCollections.push(`role_draw_group (${roleGroupsSnapshot.docs.length} documents)`);
      }
    } catch (error) {
      errors.push(`role_draw_group (createdBy): ${error.message}`);
    }

    // 6. role_draw_group 컬렉션에서 students 배열 내 UID 업데이트
    try {
      const allRoleGroupsSnapshot = await getDocs(collection(db, 'role_draw_group'));
      const batch = writeBatch(db);
      let updatedRoleGroupsCount = 0;
      
      allRoleGroupsSnapshot.docs.forEach((doc) => {
        const roleGroupData = doc.data();
        if (roleGroupData.students && Array.isArray(roleGroupData.students)) {
          let hasUpdates = false;
          
          const updatedStudents = roleGroupData.students.map(student => {
            if (student.uid === anonymousUid || student.id === anonymousUid) {
              hasUpdates = true;
              return {
                ...student,
                uid: newUid,
                id: newUid,
                email: newUser.email,
                displayName: newUser.displayName || student.displayName || '',
                migratedFrom: anonymousUid,
                migratedAt: new Date().toISOString()
              };
            }
            return student;
          });
          
          if (hasUpdates) {
            batch.update(doc.ref, { 
              students: updatedStudents,
              updatedAt: new Date().toISOString()
            });
            updatedRoleGroupsCount++;
          }
        }
      });
      
      if (updatedRoleGroupsCount > 0) {
        await batch.commit();
        migratedCollections.push(`role_draw_group (students - ${updatedRoleGroupsCount} documents)`);
      }
    } catch (error) {
      errors.push(`role_draw_group (students): ${error.message}`);
    }

    // 7. users 컬렉션 처리 (익명 사용자 데이터가 있다면 이전)
    try {
      const anonymousUserDocRef = doc(db, 'users', anonymousUid);
      const anonymousUserDoc = await getDoc(anonymousUserDocRef);
      
      if (anonymousUserDoc.exists()) {
        const anonymousUserData = anonymousUserDoc.data();
        
        // 새 사용자 데이터와 기존 익명 데이터 병합
        const mergedUserData = {
          ...anonymousUserData,
          uid: newUid,
          email: newUser.email,
          displayName: newUser.displayName || anonymousUserData.displayName || '',
          photoURL: newUser.photoURL || anonymousUserData.photoURL || '',
          provider: newUser.providerData?.[0]?.providerId || 'email',
          emailVerified: newUser.emailVerified,
          phoneNumber: newUser.phoneNumber || anonymousUserData.phoneNumber || '',
          updatedAt: new Date().toISOString(),
          migratedFrom: anonymousUid,
          migratedAt: new Date().toISOString(),
          isNewUser: false,
          profileComplete: anonymousUserData.profileComplete || false
        };
        
        // 새 UID로 사용자 문서 생성/업데이트
        const newUserDocRef = doc(db, 'users', newUid);
        await setDoc(newUserDocRef, mergedUserData, { merge: true });
        
        // 기존 익명 사용자 문서 삭제
        await deleteDoc(anonymousUserDocRef);
        
        migratedCollections.push('users');
      }
    } catch (error) {
      errors.push(`users: ${error.message}`);
    }

    return {
      success: true,
      migratedCollections,
      errors,
      anonymousUid,
      newUid
    };

  } catch (error) {
    return {
      success: false,
      migratedCollections: [],
      errors: [error.message]
    };
  }
};

// Firestore에서 사용자 정보 읽기
export const getUserFromFirestore = async (userId) => {
  try {
    if (!db) {
      return { success: false, error: "Firestore가 초기화되지 않았습니다." };
    }
    
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return { success: true, data: userSnap.data(), error: null };
    } else {
      return { success: false, data: null, error: "사용자 정보를 찾을 수 없습니다." };
    }
  } catch (error) {
    return { success: false, data: null, error: error.message };
  }
};

// Firestore에서 모든 사용자 정보 읽기 (관리자 전용)
export const getAllUsersFromFirestore = async () => {
  try {
    if (!db) {
      return { success: false, users: [], error: "Firestore가 초기화되지 않았습니다." };
    }
    
    const usersRef = collection(db, "users");
    const querySnapshot = await getDocs(usersRef);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return { success: true, users: users, error: null };
  } catch (error) {
    return { success: false, users: [], error: error.message };
  }
};

// 삭제된 사용자 목록 가져오기
export const getDeletedUsersFromFirestore = async () => {
  try {
    if (!db) {
      return { success: false, users: [], error: "Firestore가 초기화되지 않았습니다." };
    }
    
    const deletedUsersRef = collection(db, "deleted_users");
    const querySnapshot = await getDocs(deletedUsersRef);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      users.push({
        id: doc.id,
        ...data,
        // deletionStats가 있으면 최상위로 가져오기
        deletionStats: data.deletionStats || {}
      });
    });
    
    return { success: true, users: users, error: null };
  } catch (error) {
    return { success: false, users: [], error: error.message };
  }
};

// 프로필 이미지 업로드
export const uploadProfileImage = async (userId, imageFile) => {
  try {
    if (!storage) {
      return { success: false, downloadURL: null, error: "Firebase Storage가 초기화되지 않았습니다." };
    }
    
    const imageRef = ref(storage, `profile_images/${userId}`);
    const snapshot = await uploadBytes(imageRef, imageFile);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return { success: true, downloadURL: downloadURL, error: null };
  } catch (error) {
    return { success: false, downloadURL: null, error: error.message };
  }
};

// 사용자 프로필 업데이트
export const updateUserProfile = async (userId, profileData) => {
  try {
    if (!auth || !db) {
      return { success: false, error: "Firebase가 초기화되지 않았습니다." };
    }
    
    const currentUser = auth.currentUser;
    
    if (currentUser) {
      // Firebase Auth 프로필 업데이트
      await updateProfile(currentUser, {
        displayName: profileData.displayName,
        photoURL: profileData.photoURL
      });
      
      // Firestore 사용자 문서 업데이트
      const userRef = doc(db, "users", userId);
      await setDoc(userRef, {
        ...profileData,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      return { success: true, error: null };
    } else {
      return { success: false, error: "현재 로그인한 사용자가 없습니다." };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 비밀번호 변경
export const changeUserPassword = async (currentPassword, newPassword) => {
  try {
    if (!auth) {
      return { success: false, error: "Firebase Auth가 초기화되지 않았습니다." };
    }
    
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: "현재 로그인한 사용자가 없습니다." };
    }
    
    // 현재 비밀번호로 재인증
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // 새 비밀번호로 변경
    await updatePassword(user, newPassword);
    
    return { success: true, error: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 사용자 인증 제공자 확인
const getUserAuthProvider = (user) => {
  if (!user || !user.providerData || user.providerData.length === 0) {
    return 'anonymous';
  }
  
  const providerData = user.providerData[0];
  
  return {
    provider: providerData.providerId,
    uid: providerData.uid,
    email: providerData.email,
    displayName: providerData.displayName,
    photoURL: providerData.photoURL
  };
};

// 이메일/비밀번호 계정 삭제 (재인증 포함)
export const deleteUserAccountWithPassword = async (currentPassword) => {
  try {
    if (!auth) {
      return { success: false, error: "Firebase가 초기화되지 않았습니다." };
    }
    
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: "현재 로그인한 사용자가 없습니다." };
    }
    
    // 현재 비밀번호로 재인증
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // 사용자 데이터 삭제
    const deleteResult = await deleteUserData(user);
    
    // Firebase Auth에서 계정 삭제
    await deleteUser(user);
    
    return { 
      success: true, 
      error: null,
      deletedData: deleteResult
    };
  } catch (error) {
    if (error.code === 'auth/wrong-password') {
      return { success: false, error: "현재 비밀번호가 올바르지 않습니다." };
    }
    return { success: false, error: error.message };
  }
};

// Google 계정 삭제 (재인증 포함)
export const deleteUserAccountWithGoogle = async () => {
  try {
    if (!auth) {
      return { success: false, error: "Firebase가 초기화되지 않았습니다." };
    }
    
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: "현재 로그인한 사용자가 없습니다." };
    }
    
    // Google 재인증
    const provider = new GoogleAuthProvider();
    await reauthenticateWithPopup(user, provider);
    
    // 사용자 데이터 삭제
    const deleteResult = await deleteUserData(user);
    
    // Firebase Auth에서 계정 삭제
    await deleteUser(user);
    
    return { 
      success: true, 
      error: null,
      deletedData: deleteResult
    };
  } catch (reauthError) {
    if (reauthError.code === 'auth/popup-closed-by-user') {
      return { success: false, error: "Google 재인증 창이 닫혔습니다. 다시 시도해주세요." };
    }
    return { success: false, error: reauthError.message };
  }
};

// Firestore에서 사용자의 모든 데이터 삭제
const deleteAllUserDataFromFirestore = async (userId) => {
  try {
    const deletedCollections = [];
    const errors = [];
    
    // users 컬렉션에서 사용자 문서와 서브컬렉션 삭제
    try {
      // daily_saying 서브컬렉션 삭제
      const dailySayingRef = collection(db, `users/${userId}/daily_saying`);
      const dailySayingSnapshot = await getDocs(dailySayingRef);
      
      const batch = writeBatch(db);
      dailySayingSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      
      deletedCollections.push('daily_saying');
    } catch (error) {
      errors.push(`daily_saying: ${error.message}`);
    }
    
    // users 컬렉션에서 사용자 문서 삭제
    try {
      const userDocRef = doc(db, 'users', userId);
      await deleteDoc(userDocRef);
      deletedCollections.push('users');
    } catch (error) {
      errors.push(`users: ${error.message}`);
    }
    
    // teacher_test_group 컬렉션에서 사용자 문서 삭제
    try {
      const teacherTestDocRef = doc(db, 'teacher_test_group', userId);
      await deleteDoc(teacherTestDocRef);
      deletedCollections.push('teacher_test_group');
    } catch (error) {
      errors.push(`teacher_test_group: ${error.message}`);
    }
    
    // marble_drawing_member 컬렉션에서 사용자 문서 삭제 (random_picker에서 변경)
    try {
      const marbleDrawingDocRef = doc(db, 'marble_drawing_member', userId);
      await deleteDoc(marbleDrawingDocRef);
      deletedCollections.push('marble_drawing_member');
    } catch (error) {
      errors.push(`marble_drawing_member: ${error.message}`);
    }
    
    // groups 컬렉션에서 사용자가 생성한 그룹들 삭제
    try {
      const groupsQuery = query(collection(db, 'groups'), where('createdBy', '==', userId));
      const groupsSnapshot = await getDocs(groupsQuery);
      
      const batch = writeBatch(db);
      groupsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      
      deletedCollections.push('groups (created)');
    } catch (error) {
      errors.push(`groups (created): ${error.message}`);
    }
    
    // groups 컬렉션에서 사용자를 멤버에서 제거
    try {
      const allGroupsSnapshot = await getDocs(collection(db, 'groups'));
      const batch = writeBatch(db);
      
      let updatedGroupsCount = 0;
      
      allGroupsSnapshot.docs.forEach((doc) => {
        const groupData = doc.data();
        if (groupData.students && Array.isArray(groupData.students)) {
          // 사용자 UID가 포함된 학생 제거
          const updatedStudents = groupData.students.filter(student => 
            student.uid !== userId && student.id !== userId
          );
          
          if (updatedStudents.length !== groupData.students.length) {
            batch.update(doc.ref, { students: updatedStudents });
            updatedGroupsCount++;
          }
        }
      });
      
      if (updatedGroupsCount > 0) {
        await batch.commit();
      }
      
      deletedCollections.push('groups (membership)');
    } catch (error) {
      errors.push(`groups (membership): ${error.message}`);
    }
    
    // role_draw_group 컬렉션에서 사용자가 생성한 그룹들 삭제
    try {
      const roleGroupsQuery = query(collection(db, 'role_draw_group'), where('createdBy', '==', userId));
      const roleGroupsSnapshot = await getDocs(roleGroupsQuery);
      
      const batch = writeBatch(db);
      roleGroupsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      
      deletedCollections.push('role_draw_group (created)');
    } catch (error) {
      errors.push(`role_draw_group (created): ${error.message}`);
    }
    
    // role_draw_group 컬렉션에서 사용자를 멤버에서 제거
    try {
      const allRoleGroupsSnapshot = await getDocs(collection(db, 'role_draw_group'));
      const batch = writeBatch(db);
      
      let updatedRoleGroupsCount = 0;
      
      allRoleGroupsSnapshot.docs.forEach((doc) => {
        const roleGroupData = doc.data();
        if (roleGroupData.students && Array.isArray(roleGroupData.students)) {
          // 사용자 UID가 포함된 학생 제거
          const updatedStudents = roleGroupData.students.filter(student => 
            student.uid !== userId && student.id !== userId
          );
          
          if (updatedStudents.length !== roleGroupData.students.length) {
            batch.update(doc.ref, { students: updatedStudents });
            updatedRoleGroupsCount++;
          }
        }
      });
      
      if (updatedRoleGroupsCount > 0) {
        await batch.commit();
      }
      
      deletedCollections.push('role_draw_group (membership)');
    } catch (error) {
      errors.push(`role_draw_group (membership): ${error.message}`);
    }
    
    return {
      success: true,
      deletedCollections: deletedCollections,
      errors: errors
    };
  } catch (error) {
    return {
      success: false,
      deletedCollections: [],
      errors: [error.message]
    };
  }
};

// 사용자 데이터 삭제 (Firestore + Storage)
const deleteUserData = async (user) => {
  try {
    
    // 1. Firestore 데이터 삭제
    const firestoreResult = await deleteAllUserDataFromFirestore(user.uid);
    
    if (!firestoreResult.success) {
      return firestoreResult;
    }
    
    if (firestoreResult.errors && firestoreResult.errors.length > 0) {
      // 일부 오류가 있지만 계속 진행
    }
    
    // 2. Storage에서 프로필 이미지 삭제
    try {
      if (storage) {
        const profileImageRef = ref(storage, `profile_images/${user.uid}`);
        await deleteObject(profileImageRef);
      }
    } catch (storageError) {
      // 프로필 이미지가 없거나 삭제 실패 (무시)
    }
    
    return {
      success: true,
      deletedCollections: firestoreResult.deletedCollections,
      errors: firestoreResult.errors || []
    };
  } catch (error) {
    return {
      success: false,
      deletedCollections: [],
      errors: [error.message]
    };
  }
};

// 통합 계정 삭제 함수
export const deleteUserAccount = async (currentPassword = null) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: "현재 로그인한 사용자가 없습니다." };
    }
    
    // 사용자의 로그인 방법 확인
    const provider = getUserAuthProvider(user);
    
    // 로그인 방법에 따른 계정 삭제 처리
    if (provider === 'anonymous') {
      // 익명 사용자는 재인증 없이 바로 삭제
      const deleteResult = await deleteUserData(user);
      await deleteUser(user);
      return { 
        success: true, 
        error: null,
        deletedData: deleteResult
      };
    } else if (provider.provider === 'password') {
      // 이메일/비밀번호 계정
      if (!currentPassword) {
        return { success: false, error: "비밀번호를 입력해주세요." };
      }
      return await deleteUserAccountWithPassword(currentPassword);
    } else if (provider.provider === 'google.com') {
      // Google 계정
      return await deleteUserAccountWithGoogle();
    } else {
      // 기타 로그인 방법 (향후 확장 가능)
      return { success: false, error: "지원하지 않는 로그인 방법입니다." };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// 현재 사용자의 로그인 방법 확인
export const getCurrentUserProvider = () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    
    const provider = getUserAuthProvider(user);
    
    if (provider === 'anonymous') {
      return 'anonymous';
    } else if (provider.provider === 'password') {
      return 'email';
    } else if (provider.provider === 'google.com') {
      return 'google';
    } else {
      return 'unknown';
    }
  } catch (error) {
    return null;
  }
};

// ======== 이메일 중복 체크 함수 ========

// 이메일 중복 체크 함수
export const checkEmailExists = async (email) => {
  try {
    if (!auth) {
      throw new Error('Firebase Auth가 초기화되지 않았습니다.');
    }
    
    if (!email || typeof email !== 'string') {
      throw new Error('유효한 이메일 주소가 필요합니다.');
    }
    
  
    
    // Firebase Auth에서 해당 이메일로 등록된 인증 방법들을 확인
    const signInMethods = await fetchSignInMethodsForEmail(auth, email);
    
    // 인증 방법이 있으면 이미 등록된 이메일
    const exists = signInMethods && signInMethods.length > 0;
    
  
    
    return {
      exists: exists,
      signInMethods: signInMethods || [],
      message: exists ? '이미 등록된 이메일 주소입니다.' : '사용 가능한 이메일 주소입니다.'
    };
    
  } catch (error) {
    // Firebase Auth 오류 처리
    if (error.code === 'auth/invalid-email') {
      throw new Error('유효하지 않은 이메일 형식입니다.');
    } else if (error.code === 'auth/network-request-failed') {
      throw new Error('네트워크 연결을 확인해주세요.');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('이메일 중복 체크에 실패했습니다.');
    }
  }
};

// ======== Gmail 인증코드 클라이언트 함수들 ========

// 인증코드 전송
export const sendVerificationCode = async (email) => {
  try {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }
    
    if (!email) {
      throw new Error('이메일 주소가 필요합니다.');
    }
    
    // 🔥 최종 엄격한 이메일 검증 (주요 서비스 정확한 도메인 + 가짜 도메인 완전 차단)
    const strictEmailRegex = /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@(gmail\.com|outlook\.com|hotmail\.com|live\.com|msn\.com|yahoo\.com|yahoo\.co\.kr|ymail\.com|icloud\.com|me\.com|mac\.com|naver\.com|daum\.net|kakao\.com|hanmail\.net|nate\.com|aol\.com|mail\.com|gmx\.com|gmx\.de|web\.de|zoho\.com|protonmail\.com|[a-zA-Z0-9.-]+\.(com|net|org|edu|gov|co\.kr|ac\.kr|go\.kr))$/;

    const isValidEmail = (email) => {
      // 기본 검증
      if (!email || typeof email !== 'string') return false;
      if (email.length < 5 || email.length > 254) return false;
      if (email.includes('..')) return false;
      
      const [localPart, domainPart] = email.split('@');
      if (!localPart || !domainPart) return false;
      if (localPart.length > 64 || domainPart.length > 253) return false;
      if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
      
      // 🚨 포괄적인 주요 서비스 가짜 도메인 차단 (사용자 지적 문제 완전 해결)
      const comprehensiveFakePatterns = [
        // 구글 관련 (gmail.com만 정식)
        /^.+@google\..+$/,     // google.co.kr, google.net 등 모든 google.* 차단
        /^.+@gmail\.(net|org|kr|edu|gov|co\.kr)$/,
        
        // 마이크로소프트 관련 (outlook.com, hotmail.com, live.com, msn.com만 정식)
        /^.+@microsoft\..+$/,  // microsoft.* 모든 변형 차단
        /^.+@outlook\.(net|org|kr|edu|gov|co\.kr)$/,
        /^.+@hotmail\.(net|org|kr|edu|gov|co\.kr)$/,
        /^.+@live\.(net|org|kr|edu|gov|co\.kr)$/,
        /^.+@msn\.(net|org|kr|edu|gov|co\.kr)$/,
        
        // 야후 관련 (yahoo.com, yahoo.co.kr만 정식)
        /^.+@yahoo\.(net|org|kr|edu|gov)$/,
        /^.+@ymail\.(net|org|kr|edu|gov|co\.kr)$/,
        
        // 애플 관련 (icloud.com, me.com, mac.com만 정식)
        /^.+@apple\..+$/,      // apple.* 모든 변형 차단
        /^.+@icloud\.(net|org|kr|edu|gov|co\.kr)$/,
        /^.+@me\.(net|org|kr|edu|gov|co\.kr)$/,
        /^.+@mac\.(net|org|kr|edu|gov|co\.kr)$/,
        
        // 한국 서비스 관련
        /^.+@naver\.(net|org|kr|edu|gov|co\.kr|ac\.kr|go\.kr)$/,  // naver.com만 정식
        /^.+@daum\.(com|org|kr|edu|gov|co\.kr|ac\.kr|go\.kr)$/,   // daum.net만 정식
        /^.+@kakao\.(net|org|kr|edu|gov|co\.kr|ac\.kr|go\.kr)$/,  // kakao.com만 정식
        /^.+@hanmail\.(com|org|kr|edu|gov|co\.kr|ac\.kr|go\.kr)$/, // hanmail.net만 정식
        /^.+@nate\.(net|org|kr|edu|gov|co\.kr|ac\.kr|go\.kr)$/,   // nate.com만 정식
        
        // 기타 주요 서비스들
        /^.+@aol\.(net|org|kr|edu|gov|co\.kr)$/,        // aol.com만 정식
        /^.+@protonmail\.(net|org|kr|edu|gov|co\.kr)$/, // protonmail.com만 정식
        /^.+@zoho\.(net|org|kr|edu|gov|co\.kr)$/,       // zoho.com만 정식
      ];
      
      // 가짜 도메인 체크
      for (const fakePattern of comprehensiveFakePatterns) {
        if (fakePattern.test(email)) {
          return false; // 가짜 도메인 차단
        }
      }
      
      // 🎯 핵심: 엄격한 도메인 검증으로 정식 도메인만 허용
      return strictEmailRegex.test(email);
    };

    if (!isValidEmail(email)) {
      throw new Error('허용되지 않는 이메일 도메인입니다. Gmail은 gmail.com만, 네이버는 naver.com만 사용 가능합니다.');
    }
    
  
    
    const sendCode = httpsCallable(functions, 'sendVerificationCode');
    const result = await sendCode({ email });
    
  
    
    return {
      success: result.data.success,
      message: result.data.message,
      expiryTime: result.data.expiryTime,
      devCode: result.data.devCode // 개발 환경에서만 사용
    };
    
  } catch (error) {
    // Firebase Functions 오류 메시지 처리
    if (error.code === 'unauthenticated') {
      throw new Error('인증이 필요합니다.');
    } else if (error.code === 'permission-denied') {
      throw new Error('권한이 없습니다.');
    } else if (error.code === 'unavailable') {
      throw new Error('서비스가 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해주세요.');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('인증코드 전송에 실패했습니다.');
    }
  }
};

// 인증코드 검증
export const verifyEmailCode = async (email, code) => {
  try {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }
    
    if (!email || !code) {
      throw new Error('이메일과 인증코드가 필요합니다.');
    }
    
  
    
    const verifyCode = httpsCallable(functions, 'verifyCode');
    const result = await verifyCode({ email, code });
    

    
    return {
      success: result.data.success,
      message: result.data.message,
      email: result.data.email,
      verifiedAt: result.data.verifiedAt
    };
    
  } catch (error) {
    // Firebase Functions 오류 메시지 처리
    if (error.code === 'unauthenticated') {
      throw new Error('인증이 필요합니다.');
    } else if (error.code === 'permission-denied') {
      throw new Error('권한이 없습니다.');
    } else if (error.code === 'unavailable') {
      throw new Error('서비스가 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해주세요.');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('인증코드 검증에 실패했습니다.');
    }
  }
};

// 인증코드 재전송
export const resendVerificationCode = async (email) => {
  try {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }
    
    if (!email) {
      throw new Error('이메일 주소가 필요합니다.');
    }
    
  
    
    const resendCode = httpsCallable(functions, 'resendVerificationCode');
    const result = await resendCode({ email });
    
  
    
    return {
      success: result.data.success,
      message: result.data.message,
      expiryTime: result.data.expiryTime,
      devCode: result.data.devCode // 개발 환경에서만 사용
    };
    
  } catch (error) {
    // Firebase Functions 오류 메시지 처리
    if (error.code === 'unauthenticated') {
      throw new Error('인증이 필요합니다.');
    } else if (error.code === 'permission-denied') {
      throw new Error('권한이 없습니다.');
    } else if (error.code === 'unavailable') {
      throw new Error('서비스가 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해주세요.');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('인증코드 재전송에 실패했습니다.');
    }
  }
};

// 인증코드가 포함된 회원가입 함수
export const registerWithEmailVerification = async (email, password, verificationCode, additionalData = {}) => {
  try {
    // 1단계: 인증코드 검증
    const verifyResult = await verifyEmailCode(email, verificationCode);
    
    if (!verifyResult.success) {
      throw new Error('이메일 인증에 실패했습니다.');
    }
    
    // 2단계: 기존 회원가입 로직 실행 (마케팅 동의 정보 포함)
    const registerResult = await registerWithEmailAndPassword(email, password, additionalData);
    
    if (registerResult.error) {
      throw new Error(registerResult.error);
    }
    

    
    return {
      user: registerResult.user,
      error: null,
      isNewUser: registerResult.isNewUser,
      userData: registerResult.userData,
      emailVerified: true // 인증코드로 검증됨
    };
    
  } catch (error) {
    return {
      user: null,
      error: error.message,
      isNewUser: false,
      emailVerified: false
    };
  }
};

// 마케팅 동의 정보 업데이트
export const updateMarketingConsent = async (agreeMarketing) => {
  try {
    if (!db) {
      throw new Error("Firestore가 초기화되지 않았습니다.");
    }
    
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error("로그인된 사용자가 없습니다.");
    }
    
    const nowIso = new Date().toISOString();
    const updateData = {
      agreeMarketing: agreeMarketing,
      marketingConsentAt: agreeMarketing ? nowIso : null,
      marketingRespondedAt: nowIso,
      updatedAt: nowIso,
    };
    
    const userRef = doc(db, "users", currentUser.uid);
    await setDoc(userRef, updateData, { merge: true });
    
    // Analytics 이벤트 로깅
    await logAnalyticsEvent('marketing_consent_update', {
      user_id: currentUser.uid,
      marketing_consent: agreeMarketing,
      consent_method: 'modal'
    });
    

    
    return { 
      success: true, 
      error: null,
      data: updateData
    };
  } catch (error) {
    throw error;
  }
};

// teachering-class DB: 학급 관리, 학생 관리, 마블뽑기 등에 사용
export const teachClassDB = getFirestore(app, 'teachering-class');

// 🔥 Emulator 연결 설정 (개발 환경에서만)
// REACT_APP_USE_EMULATOR 환경변수가 'true'일 때만 emulator에 연결
if (process.env.REACT_APP_USE_EMULATOR === 'true') {
  try {
    // Firestore Emulator 연결 (기본 DB)
    if (db) {
      connectFirestoreEmulator(db, 'localhost', 8080);
    }
    
    // Firestore Emulator 연결 (teachering-class DB)
    if (teachClassDB) {
      connectFirestoreEmulator(teachClassDB, 'localhost', 8080);
    }
    
    // Auth Emulator 연결
    if (auth) {
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    }
    
    // Functions Emulator 연결
    if (functions) {
      connectFunctionsEmulator(functions, 'localhost', 5001);
    }
    
    // Storage Emulator 연결
    if (storage) {
      connectStorageEmulator(storage, 'localhost', 9000);
    }
  } catch (error) {
    // Emulator 연결 오류 무시
  }
}

// ==========================================
// 쿠폰 관리 API
// ==========================================

/**
 * 쿠폰 생성
 */
export const createCoupon = async (couponData) => {
  if (!db) {
    throw new Error('Firestore가 초기화되지 않았습니다.');
  }

  try {
    const { 
      couponCode, 
      couponName, 
      proMonths, 
      proExpiryDate,
      validUntil, 
      isMultiUse, 
      maxUses, 
      createdBy 
    } = couponData;

    // 쿠폰 코드 중복 체크
    const couponRef = doc(db, 'coupons', couponCode);
    const couponSnap = await getDoc(couponRef);

    if (couponSnap.exists()) {
      return {
        success: false,
        error: '이미 존재하는 쿠폰 코드입니다.'
      };
    }

    const now = new Date();
    const coupon = {
      couponCode,
      couponName,
      proMonths: proMonths || null,
      proExpiryDate: proExpiryDate || null,
      validUntil: validUntil || null,
      isMultiUse: isMultiUse || false,
      maxUses: isMultiUse ? (maxUses || 999999) : 1,
      currentUses: 0,
      usedBy: [],
      createdAt: now.toISOString(),
      createdBy: createdBy || 'admin',
      isActive: true
    };

    await setDoc(couponRef, coupon);

    return {
      success: true,
      data: coupon
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || '쿠폰 생성 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 모든 쿠폰 조회
 */
export const getAllCoupons = async () => {
  if (!db) {
    throw new Error('Firestore가 초기화되지 않았습니다.');
  }

  try {
    const couponsRef = collection(db, 'coupons');
    const q = query(couponsRef, where('isActive', '==', true));
    const querySnapshot = await getDocs(q);

    const coupons = [];
    querySnapshot.forEach((doc) => {
      coupons.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return {
      success: true,
      coupons
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || '쿠폰 목록을 불러오는데 실패했습니다.',
      coupons: []
    };
  }
};

/**
 * 쿠폰 삭제 (소프트 삭제)
 */
export const deleteCoupon = async (couponCode) => {
  if (!db) {
    throw new Error('Firestore가 초기화되지 않았습니다.');
  }

  try {
    const couponRef = doc(db, 'coupons', couponCode);
    await setDoc(couponRef, { isActive: false, deletedAt: new Date().toISOString() }, { merge: true });

    return {
      success: true
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || '쿠폰 삭제 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 쿠폰 사용 내역 조회
 */
export const getCouponUsages = async (couponCode = null) => {
  if (!db) {
    throw new Error('Firestore가 초기화되지 않았습니다.');
  }

  try {
    const usagesRef = collection(db, 'couponUsages');
    let q;
    
    if (couponCode) {
      q = query(usagesRef, where('couponCode', '==', couponCode));
    } else {
      q = query(usagesRef);
    }

    const querySnapshot = await getDocs(q);

    const usages = [];
    querySnapshot.forEach((doc) => {
      usages.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // 최신순 정렬
    usages.sort((a, b) => new Date(b.usedAt) - new Date(a.usedAt));

    return {
      success: true,
      usages
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || '쿠폰 사용 내역을 불러오는데 실패했습니다.',
      usages: []
    };
  }
};

/**
 * 쿠폰 사용 취소 (프로 권한 제거 포함) - 트랜잭션 처리
 */
export const cancelCouponUsage = async (usageId, userId, couponCode) => {
  if (!db) {
    throw new Error('Firestore가 초기화되지 않았습니다.');
  }

  try {
    // 1. 트랜잭션 전에 활성 구독 확인 (결제로 얻은 프로 권한)
    const subscriptionsRef = collection(db, 'subscriptions');
    const q = query(subscriptionsRef, where('userId', '==', userId), where('status', '==', 'active'));
    const subscriptionsSnapshot = await getDocs(q);
    const hasActiveSubscription = !subscriptionsSnapshot.empty;
    
    // 2. 단일 트랜잭션으로 쿠폰 취소 처리 (Read -> Write 순서 보장)
    await runTransaction(db, async (transaction) => {
      // === 모든 READ 작업 먼저 수행 ===
      
      // 사용 내역 조회
      const usageRef = doc(db, 'couponUsages', usageId);
      const usageDoc = await transaction.get(usageRef);
      
      if (!usageDoc.exists()) {
        throw new Error('사용 내역을 찾을 수 없습니다.');
      }

      // 쿠폰 정보 조회
      const couponRef = doc(db, 'coupons', couponCode);
      const couponDoc = await transaction.get(couponRef);

      // === 2. 모든 WRITE 작업 수행 ===
      
      // 사용 내역 삭제
      transaction.delete(usageRef);

      // 쿠폰의 currentUses 감소 및 usedBy에서 제거
      if (couponDoc.exists()) {
        const couponData = couponDoc.data();
        const updatedUsedBy = (couponData.usedBy || []).filter(id => id !== userId);
        const updatedCurrentUses = Math.max(0, (couponData.currentUses || 0) - 1);

        transaction.update(couponRef, {
          currentUses: updatedCurrentUses,
          usedBy: updatedUsedBy
        });
      }

      // 프로 권한 처리: 활성 구독이 없는 경우에만 제거
      if (!hasActiveSubscription) {
        const userRef = doc(db, 'users', userId);
        const entitlementsRef = doc(db, 'entitlements', userId);

        // users 컬렉션 업데이트
        transaction.set(userRef, {
          isPro: false,
          proExpiresAt: null,
          proDowngradedAt: new Date(),
          proDowngradedBy: 'coupon_cancellation',
          proDowngradedReason: `쿠폰 취소: ${couponCode}`
        }, { merge: true });

        // entitlements 컬렉션 업데이트
        transaction.set(entitlementsRef, {
          userId: userId,
          plan: 'basic',
          active: false,
          status: 'canceled',
          subscriptionId: null,
          expiresAt: null,
          grantedByAdmin: false,
          grantedByCoupon: false,
          couponCode: null,
          updatedAt: new Date()
        }, { merge: true });
      } else {
        // 활성 구독이 있으면 쿠폰 관련 정보만 제거하고 프로 권한은 유지
        const userRef = doc(db, 'users', userId);
        transaction.set(userRef, {
          proUpgradedCoupon: null,
          couponCancelledAt: new Date()
        }, { merge: true });
      }
    });

    if (hasActiveSubscription) {
      return {
        success: true,
        message: '쿠폰 사용이 취소되었습니다. 활성 구독이 있어 프로 권한은 유지됩니다.',
        data: {
          couponCancelled: true,
          proRemoved: false,
          reason: 'active_subscription_exists'
        }
      };
    } else {
      return {
        success: true,
        message: '쿠폰 사용이 취소되고 프로 권한이 제거되었습니다.',
        data: {
          couponCancelled: true,
          proRemoved: true
        }
      };
    }

  } catch (error) {
    return {
      success: false,
      error: error.message || '쿠폰 사용 취소 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 쿠폰 사용 (클라이언트에서는 검증만, 실제 적용은 서버에서)
 */
export const validateCoupon = async (couponCode, userId) => {
  if (!db) {
    throw new Error('Firestore가 초기화되지 않았습니다.');
  }

  try {
    const couponRef = doc(db, 'coupons', couponCode);
    const couponSnap = await getDoc(couponRef);

    if (!couponSnap.exists()) {
      return {
        success: false,
        error: '존재하지 않는 쿠폰 코드입니다.'
      };
    }

    const coupon = couponSnap.data();

    // 활성화 체크
    if (!coupon.isActive) {
      return {
        success: false,
        error: '사용할 수 없는 쿠폰입니다.'
      };
    }

    // 유효기간 체크
    if (coupon.validUntil) {
      const validDate = new Date(coupon.validUntil);
      const now = new Date();
      if (now > validDate) {
        return {
          success: false,
          error: '유효기간이 만료된 쿠폰입니다.'
        };
      }
    }

    // 사용 가능 횟수 체크 (maxUses가 null이면 무제한)
    if (coupon.maxUses !== null && coupon.currentUses >= coupon.maxUses) {
      return {
        success: false,
        error: '사용 가능 횟수를 초과한 쿠폰입니다.'
      };
    }

    // 사용자별 중복 사용 체크
    if (coupon.usedBy && coupon.usedBy.includes(userId)) {
      return {
        success: false,
        error: '이미 사용한 쿠폰입니다.'
      };
    }

    return {
      success: true,
      data: coupon
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || '쿠폰 검증 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 쿠폰 사용 (프로 권한 부여 포함) - 트랜잭션 처리
 */
export const applyCoupon = async (couponCode, userId, userEmail, userName) => {
  if (!db || !auth) {
    throw new Error('Firebase가 초기화되지 않았습니다.');
  }

  try {
    // 1. 먼저 쿠폰 검증
    const validationResult = await validateCoupon(couponCode, userId);
    if (!validationResult.success) {
      return validationResult;
    }

    const coupon = validationResult.data;

    // 2. 트랜잭션으로 쿠폰 사용 처리
    let usageId = null;
    try {
      await runTransaction(db, async (transaction) => {
        const couponRef = doc(db, 'coupons', couponCode);
        const couponDoc = await transaction.get(couponRef);

        if (!couponDoc.exists()) {
          throw new Error('쿠폰이 존재하지 않습니다.');
        }

        const couponData = couponDoc.data();

        // 다시 한 번 검증 (동시성 제어, maxUses가 null이면 무제한)
        if (couponData.maxUses !== null && couponData.currentUses >= couponData.maxUses) {
          throw new Error('사용 가능 횟수를 초과한 쿠폰입니다.');
        }

        if (couponData.usedBy && couponData.usedBy.includes(userId)) {
          throw new Error('이미 사용한 쿠폰입니다.');
        }

        // 쿠폰 사용 횟수 증가 및 사용자 추가
        transaction.update(couponRef, {
          currentUses: (couponData.currentUses || 0) + 1,
          usedBy: [...(couponData.usedBy || []), userId]
        });

        // 사용 내역 저장
        const usageRef = doc(collection(db, 'couponUsages'));
        usageId = usageRef.id;
        transaction.set(usageRef, {
          couponCode,
          userId,
          userEmail: userEmail || '',
          userName: userName || '',
          usedAt: new Date().toISOString(),
          proMonths: couponData.proMonths || null,
          proExpiryDate: couponData.proExpiryDate || null
        });
      });
    } catch (transactionError) {
      return {
        success: false,
        error: transactionError.message || '쿠폰 사용 처리 중 오류가 발생했습니다.'
      };
    }

    // 3. 프로 권한 부여 (Firestore 직접 업데이트)
    try {
      // 프로 만료일 계산
      let proExpiryDate;
      if (coupon.proMonths) {
        // 개월수로 지정된 경우
        const expiryDate = new Date();
        expiryDate.setMonth(expiryDate.getMonth() + coupon.proMonths);
        proExpiryDate = expiryDate;
      } else if (coupon.proExpiryDate) {
        // 날짜로 지정된 경우
        proExpiryDate = new Date(coupon.proExpiryDate);
      } else {
        throw new Error('쿠폰에 프로 기간 정보가 없습니다.');
      }

      // Firestore에 직접 프로 권한 업데이트
      const userRef = doc(db, 'users', userId);
      const entitlementsRef = doc(db, 'entitlements', userId);
      
      // 트랜잭션으로 users와 entitlements 동시 업데이트
      await runTransaction(db, async (transaction) => {
        // users 컬렉션 업데이트
        transaction.set(userRef, {
          isPro: true,
          proExpiresAt: proExpiryDate,
          proUpgradedAt: new Date(),
          proUpgradedBy: 'coupon',
          proUpgradedCoupon: couponCode
        }, { merge: true });

        // entitlements 컬렉션 업데이트
        transaction.set(entitlementsRef, {
          userId: userId,
          plan: 'pro',
          active: true,
          status: 'active',
          subscriptionId: null, // 쿠폰은 구독이 아님
          expiresAt: proExpiryDate,
          grantedByAdmin: false,
          grantedByCoupon: true,
          couponCode: couponCode,
          updatedAt: new Date()
        }, { merge: true });
      });

      return {
        success: true,
        message: '쿠폰이 성공적으로 사용되었습니다!',
        data: {
          proExpiryDate: proExpiryDate.toISOString().split('T')[0],
          couponName: coupon.couponName,
          proMonths: coupon.proMonths
        }
      };

    } catch (firestoreError) {
      // Firestore 업데이트 실패 시 쿠폰 사용 롤백
      await rollbackCouponUsage(usageId, userId, couponCode);
      
      return {
        success: false,
        error: '프로 권한 부여 중 오류가 발생했습니다. 쿠폰 사용이 취소되었습니다.'
      };
    }

  } catch (error) {
    return {
      success: false,
      error: error.message || '쿠폰 사용 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 쿠폰 사용 롤백 (프로 권한 부여 실패 시)
 */
const rollbackCouponUsage = async (usageId, userId, couponCode) => {
  if (!db) return;

  try {
    // 트랜잭션으로 롤백 처리
    await runTransaction(db, async (transaction) => {
      // 사용 내역 삭제
      if (usageId) {
        const usageRef = doc(db, 'couponUsages', usageId);
        transaction.delete(usageRef);
      }

      // 쿠폰의 currentUses 감소 및 usedBy에서 제거
      const couponRef = doc(db, 'coupons', couponCode);
      const couponDoc = await transaction.get(couponRef);

      if (couponDoc.exists()) {
        const couponData = couponDoc.data();
        const updatedUsedBy = (couponData.usedBy || []).filter(id => id !== userId);
        const updatedCurrentUses = Math.max(0, (couponData.currentUses || 0) - 1);

        transaction.update(couponRef, {
          currentUses: updatedCurrentUses,
          usedBy: updatedUsedBy
        });
      }
    });
  } catch (rollbackError) {
    // 롤백 오류 무시
  }
};

// ============================================
// 게시물 관리 API
// ============================================

/**
 * 게시물 생성
 * @param {string} postType - 게시물 종류 ('events', 'update_and_notice', 'FAQ')
 * @param {object} postData - 게시물 데이터 { title, content, category, date, accessLevel, specificUsers, imageURLs }
 * @param {string} userId - 생성자 ID
 */
export const createNoticePost = async (postType, postData, userId) => {
  if (!db) {
    return { success: false, error: 'Firestore가 초기화되지 않았습니다.' };
  }

  try {
    // 게시물 ID 생성 (타임스탬프 기반)
    const postId = `post_${Date.now()}`;
    const postRef = doc(db, 'notice_board', userId, postType, postId);

    const newPost = {
      id: postId,
      title: postData.title || '',
      content: postData.content || '',
      category: postData.category || '',
      date: postData.date || new Date().toISOString().split('T')[0],
      accessLevel: postData.accessLevel || 'all', // 'all', 'member', 'pro', 'specific'
      specificUsers: postData.specificUsers || [], // accessLevel이 'specific'일 때 사용
      imageURLs: postData.imageURLs || [], // 이미지 URL 배열
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: userId
    };

    // 이벤트 타입일 경우 startDate, endDate 추가
    if (postType === 'events') {
      if (postData.startDate) newPost.startDate = postData.startDate;
      if (postData.endDate) newPost.endDate = postData.endDate;
    }

    // 업데이트 공지 타입일 경우 expiryDate, unlimited 추가
    if (postType === 'update_and_notice') {
      newPost.unlimited = postData.unlimited || false;
      if (!postData.unlimited && postData.expiryDate) {
        newPost.expiryDate = postData.expiryDate;
      }
    }

    // FAQ 타입일 경우 question, answer 필드 추가
    if (postType === 'FAQ') {
      newPost.question = postData.question || postData.title;
      newPost.answer = postData.answer || postData.content;
    }

    await setDoc(postRef, newPost);

    return {
      success: true,
      message: '게시물이 성공적으로 생성되었습니다.',
      data: newPost
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '게시물 생성 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 게시물 수정
 * @param {string} postType - 게시물 종류
 * @param {string} postId - 게시물 ID
 * @param {object} updateData - 수정할 데이터 (imageURLs, removedImageURLs 포함 가능)
 * @param {string} userId - 작성자 ID
 */
export const updateNoticePost = async (postType, postId, updateData, userId) => {
  if (!db) {
    return { success: false, error: 'Firestore가 초기화되지 않았습니다.' };
  }

  try {
    const postRef = doc(db, 'notice_board', userId, postType, postId);
    
    // 기존 게시물 데이터 가져오기
    const postDoc = await getDoc(postRef);
    let oldImageURLs = [];
    
    if (postDoc.exists()) {
      const oldData = postDoc.data();
      
      // 기존 imageURLs 배열 (이전 방식)
      if (oldData.imageURLs && oldData.imageURLs.length > 0) {
        oldImageURLs = [...oldData.imageURLs];
      }
      
      // 기존 HTML 콘텐츠에서 이미지 추출
      if (oldData.content) {
        const oldContentImages = extractImageURLsFromHTML(oldData.content);
        oldImageURLs = [...oldImageURLs, ...oldContentImages];
      }
      
      // 기존 answer 필드에서 이미지 추출 (FAQ용)
      if (oldData.answer) {
        const oldAnswerImages = extractImageURLsFromHTML(oldData.answer);
        oldImageURLs = [...oldImageURLs, ...oldAnswerImages];
      }
      
      // 중복 제거
      oldImageURLs = [...new Set(oldImageURLs)];
    }
    
    // 새로운 콘텐츠에서 이미지 추출
    let newImageURLs = [];
    
    if (updateData.content) {
      const newContentImages = extractImageURLsFromHTML(updateData.content);
      newImageURLs = [...newImageURLs, ...newContentImages];
    }
    
    // FAQ의 경우 answer 필드도 확인
    if (postType === 'FAQ' && updateData.content) {
      const newAnswerImages = extractImageURLsFromHTML(updateData.content);
      newImageURLs = [...newImageURLs, ...newAnswerImages];
    }
    
    // 새로운 imageURLs 배열 (이전 방식 호환)
    if (updateData.imageURLs) {
      newImageURLs = [...newImageURLs, ...updateData.imageURLs];
    }
    
    // 중복 제거
    newImageURLs = [...new Set(newImageURLs)];
    
    // 삭제할 이미지 찾기: 기존에는 있었지만 새로운 것에는 없는 이미지
    const imagesToDelete = oldImageURLs.filter(url => !newImageURLs.includes(url));
    
    // 명시적으로 제거할 이미지도 추가
    if (updateData.removedImageURLs && updateData.removedImageURLs.length > 0) {
      imagesToDelete.push(...updateData.removedImageURLs);
    }
    
    // 중복 제거
    const uniqueImagesToDelete = [...new Set(imagesToDelete)];
    
    // 이미지 삭제
    if (uniqueImagesToDelete.length > 0) {
      await deleteNoticePostImages(uniqueImagesToDelete);
    }
    
    const updatedData = {
      ...updateData,
      updatedAt: new Date().toISOString()
    };

    // removedImageURLs는 Firestore에 저장하지 않음
    delete updatedData.removedImageURLs;

    // imageURLs가 있으면 그대로 사용 (업데이트)
    if (updateData.imageURLs !== undefined) {
      updatedData.imageURLs = updateData.imageURLs;
    }

    // 이벤트 타입일 경우 startDate, endDate 처리
    if (postType === 'events') {
      if (updateData.startDate !== undefined) updatedData.startDate = updateData.startDate;
      if (updateData.endDate !== undefined) updatedData.endDate = updateData.endDate;
    }

    // 업데이트 공지 타입일 경우 expiryDate, unlimited 처리
    if (postType === 'update_and_notice') {
      if (updateData.unlimited !== undefined) {
        updatedData.unlimited = updateData.unlimited;
      }
      if (updateData.expiryDate !== undefined) {
        updatedData.expiryDate = updateData.expiryDate;
      }
    }

    // FAQ 타입일 경우 question, answer도 업데이트
    if (postType === 'FAQ') {
      if (updateData.title) {
        updatedData.question = updateData.title;
      }
      if (updateData.content) {
        updatedData.answer = updateData.content;
      }
    }

    await setDoc(postRef, updatedData, { merge: true });

    return {
      success: true,
      message: '게시물이 성공적으로 수정되었습니다.',
      data: updatedData
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '게시물 수정 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 게시물 삭제
 * @param {string} postType - 게시물 종류
 * @param {string} postId - 게시물 ID
 * @param {string} userId - 작성자 ID
 */
/**
 * HTML 콘텐츠에서 이미지 URL 추출
 * @param {string} htmlContent - HTML 콘텐츠
 * @returns {string[]} 이미지 URL 배열
 */
const extractImageURLsFromHTML = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return [];
  }

  const imageURLs = [];
  // <img src="..."> 패턴에서 src 속성 추출
  const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
  let match;

  while ((match = imgRegex.exec(htmlContent)) !== null) {
    const url = match[1];
    // Firebase Storage URL만 추출
    if (url.includes('firebasestorage.googleapis.com')) {
      imageURLs.push(url);
    }
  }

  return imageURLs;
};

export const deleteNoticePost = async (postType, postId, userId) => {
  if (!db) {
    return { success: false, error: 'Firestore가 초기화되지 않았습니다.' };
  }

  try {
    const postRef = doc(db, 'notice_board', userId, postType, postId);
    
    // 먼저 게시물 데이터를 가져와서 이미지 URL 확인
    const postDoc = await getDoc(postRef);
    
    if (postDoc.exists()) {
      const postData = postDoc.data();
      
      // 1. 기존 imageURLs 배열에서 이미지 삭제 (이전 방식 호환)
      let imageURLsToDelete = [];
      
      if (postData.imageURLs && postData.imageURLs.length > 0) {
        imageURLsToDelete = [...postData.imageURLs];
      }
      
      // 2. HTML 콘텐츠에서 이미지 URL 추출 (리치 텍스트 에디터)
      if (postData.content) {
        const htmlImageURLs = extractImageURLsFromHTML(postData.content);
        imageURLsToDelete = [...imageURLsToDelete, ...htmlImageURLs];
      }
      
      // 3. answer 필드에서도 이미지 추출 (FAQ용)
      if (postData.answer) {
        const answerImageURLs = extractImageURLsFromHTML(postData.answer);
        imageURLsToDelete = [...imageURLsToDelete, ...answerImageURLs];
      }
      
      // 중복 제거
      imageURLsToDelete = [...new Set(imageURLsToDelete)];
      
      // 이미지 삭제
      if (imageURLsToDelete.length > 0) {
        await deleteNoticePostImages(imageURLsToDelete);
      }
    }
    
    // 게시물 문서 삭제
    await deleteDoc(postRef);

    return {
      success: true,
      message: '게시물이 성공적으로 삭제되었습니다.'
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '게시물 삭제 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 특정 타입의 모든 게시물 조회
 * @param {string} postType - 게시물 종류
 */
export const getNoticePostsByType = async (postType) => {
  if (!db) {
    return { success: false, error: 'Firestore가 초기화되지 않았습니다.' };
  }

  try {
    const posts = [];
    
    // Collection Group Query를 사용하여 모든 postType 서브컬렉션 조회
    const postsQuery = collectionGroup(db, postType);
    const postsSnapshot = await getDocs(postsQuery);

    postsSnapshot.forEach((postDoc) => {
      const postData = postDoc.data();
      
      // 문서 경로에서 userId 추출
      // 경로 형식: notice_board/{userId}/{postType}/{postId}
      const pathSegments = postDoc.ref.path.split('/');
      const userId = pathSegments[1]; // notice_board 다음이 userId
      
      const fullPostData = {
        ...postData,
        userId: userId // 작성자 ID 포함
      };
      
      posts.push(fullPostData);
    });

    // 날짜순 정렬 (최신순)
    posts.sort((a, b) => new Date(b.date) - new Date(a.date));

    return {
      success: true,
      data: posts
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '게시물 조회 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 특정 게시물 단일 조회
 * @param {string} postType - 게시물 종류
 * @param {string} postId - 게시물 ID
 * @param {string} userId - 작성자 ID
 */
export const getNoticePost = async (postType, postId, userId) => {
  if (!db) {
    return { success: false, error: 'Firestore가 초기화되지 않았습니다.' };
  }

  try {
    const postRef = doc(db, 'notice_board', userId, postType, postId);
    const postDoc = await getDoc(postRef);

    if (!postDoc.exists()) {
      return {
        success: false,
        error: '게시물을 찾을 수 없습니다.'
      };
    }

    return {
      success: true,
      data: postDoc.data()
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '게시물 조회 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 모든 게시물 조회 (모든 타입)
 */
export const getAllNoticePosts = async () => {
  if (!db) {
    return { success: false, error: 'Firestore가 초기화되지 않았습니다.' };
  }

  try {
    const allPosts = {
      events: [],
      update_and_notice: [],
      FAQ: []
    };

    // 각 타입별로 게시물 조회
    const types = ['events', 'update_and_notice', 'FAQ'];
    
    for (const type of types) {
      const result = await getNoticePostsByType(type);
      if (result.success) {
        allPosts[type] = result.data;
      }
    }

    return {
      success: true,
      data: allPosts
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '게시물 조회 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 사용자 권한에 따른 게시물 필터링
 * @param {array} posts - 게시물 배열
 * @param {object} user - 사용자 객체 (isAdmin, isPro 등 포함)
 * @param {string} userId - 사용자 ID
 */
export const filterPostsByAccess = (posts, user, userId) => {
  if (!posts || !Array.isArray(posts)) return [];

  return posts.filter(post => {
    // accessLevel이 없거나 'all'인 경우 모두 공개
    if (!post.accessLevel || post.accessLevel === 'all') {
      return true;
    }

    // 로그인하지 않은 경우
    if (!user || !userId) {
      return post.accessLevel === 'all';
    }

    // 회원 전용
    if (post.accessLevel === 'member') {
      return true; // 로그인한 사용자는 모두 볼 수 있음
    }

    // 프로 사용자 전용
    if (post.accessLevel === 'pro') {
      return user.isPro === true || user.isAdmin === true;
    }

    // 관리자 전용
    if (post.accessLevel === 'admin') {
      return user.isAdmin === true;
    }

    // 특정 사용자 전용
    if (post.accessLevel === 'specific') {
      return post.specificUsers && post.specificUsers.includes(userId);
    }

    return false;
  });
};

// ============================================
// 알림 관리 API
// ============================================

/**
 * 알림 생성 (게시물 작성 시)
 * @param {object} notificationData - 알림 데이터
 * @param {array} targetUsers - 대상 사용자 ID 배열
 */
export const createNotifications = async (notificationData, targetUsers) => {
  if (!db) {
    return { success: false, error: 'Firestore가 초기화되지 않았습니다.' };
  }

  try {
    const batch = writeBatch(db);
    const notificationId = `notif_${Date.now()}`;
    let createdCount = 0;

    // 각 대상 사용자에게 알림 생성
    for (const userId of targetUsers) {
      const notificationRef = doc(db, 'notifications', userId, 'items', notificationId);
      
      const notification = {
        id: notificationId,
        postId: notificationData.postId,
        postType: notificationData.postType,
        title: notificationData.title,
        message: notificationData.message || notificationData.title,
        category: notificationData.category || '',
        isRead: false,
        createdAt: new Date().toISOString(),
        // 게시물로 이동할 URL 정보
        postUrl: getPostUrl(notificationData.postType, notificationData.postId)
      };

      batch.set(notificationRef, notification);
      createdCount++;
    }

    await batch.commit();

    return {
      success: true,
      message: `${createdCount}명의 사용자에게 알림이 전송되었습니다.`,
      createdCount
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '알림 생성 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 게시물 타입에 따른 URL 생성
 */
const getPostUrl = (postType, postId) => {
  switch (postType) {
    case 'events':
      return `/help-center/events/${postId}`;
    case 'update_and_notice':
      return `/help-center/post/${postId}`;
    case 'FAQ':
      return `/help-center/faq/${postId}`;
    default:
      return '/help-center';
  }
};

/**
 * 사용자의 읽지 않은 알림 조회
 * @param {string} userId - 사용자 ID
 */
export const getUnreadNotifications = async (userId) => {
  if (!db || !userId) {
    return { success: false, error: 'Firestore 또는 사용자 ID가 없습니다.' };
  }

  try {
    const notificationsRef = collection(db, 'notifications', userId, 'items');
    const q = query(notificationsRef, where('isRead', '==', false));
    const querySnapshot = await getDocs(q);

    const notifications = [];
    querySnapshot.forEach((doc) => {
      notifications.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // 최신순 정렬
    notifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return {
      success: true,
      data: notifications,
      count: notifications.length
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '알림 조회 중 오류가 발생했습니다.',
      data: [],
      count: 0
    };
  }
};

/**
 * 사용자의 모든 알림 조회 (읽음/안읽음 포함)
 * @param {string} userId - 사용자 ID
 * @param {number} limit - 조회할 알림 수 (기본 20개)
 */
export const getAllNotifications = async (userId, limit = 20) => {
  if (!db || !userId) {
    return { success: false, error: 'Firestore 또는 사용자 ID가 없습니다.' };
  }

  try {
    const notificationsRef = collection(db, 'notifications', userId, 'items');
    const querySnapshot = await getDocs(notificationsRef);

    const notifications = [];
    querySnapshot.forEach((doc) => {
      notifications.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // 최신순 정렬 및 제한
    notifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const limitedNotifications = notifications.slice(0, limit);

    return {
      success: true,
      data: limitedNotifications,
      total: notifications.length
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '알림 조회 중 오류가 발생했습니다.',
      data: [],
      total: 0
    };
  }
};

/**
 * 알림 읽음 처리
 * @param {string} userId - 사용자 ID
 * @param {string} notificationId - 알림 ID
 */
export const markNotificationAsRead = async (userId, notificationId) => {
  if (!db || !userId || !notificationId) {
    return { success: false, error: '필수 파라미터가 없습니다.' };
  }

  try {
    const notificationRef = doc(db, 'notifications', userId, 'items', notificationId);
    await setDoc(notificationRef, {
      isRead: true,
      readAt: new Date().toISOString()
    }, { merge: true });

    return {
      success: true,
      message: '알림이 읽음 처리되었습니다.'
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '알림 읽음 처리 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 모든 알림 읽음 처리
 * @param {string} userId - 사용자 ID
 */
export const markAllNotificationsAsRead = async (userId) => {
  if (!db || !userId) {
    return { success: false, error: '사용자 ID가 없습니다.' };
  }

  try {
    const notificationsRef = collection(db, 'notifications', userId, 'items');
    const q = query(notificationsRef, where('isRead', '==', false));
    const querySnapshot = await getDocs(q);

    const batch = writeBatch(db);
    let updatedCount = 0;

    querySnapshot.forEach((doc) => {
      batch.update(doc.ref, {
        isRead: true,
        readAt: new Date().toISOString()
      });
      updatedCount++;
    });

    if (updatedCount > 0) {
      await batch.commit();
    }

    return {
      success: true,
      message: `${updatedCount}개의 알림이 읽음 처리되었습니다.`,
      updatedCount
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '알림 읽음 처리 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 알림 삭제
 * @param {string} userId - 사용자 ID
 * @param {string} notificationId - 알림 ID
 */
export const deleteNotification = async (userId, notificationId) => {
  if (!db || !userId || !notificationId) {
    return { success: false, error: '필수 파라미터가 없습니다.' };
  }

  try {
    const notificationRef = doc(db, 'notifications', userId, 'items', notificationId);
    await deleteDoc(notificationRef);

    return {
      success: true,
      message: '알림이 삭제되었습니다.'
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '알림 삭제 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 특정 게시물과 관련된 모든 알림 삭제
 * @param {string} postId - 삭제할 게시물 ID
 */
export const deleteNotificationsByPostId = async (postId) => {
  if (!db || !postId) {
    return { success: false, error: '필수 파라미터가 없습니다.' };
  }

  try {
    // 모든 사용자 목록 조회
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    
    const batch = writeBatch(db);
    let deletedCount = 0;

    // 각 사용자의 알림을 확인
    for (const userDoc of usersSnapshot.docs) {
      const userId = userDoc.id;
      const notificationsRef = collection(db, 'notifications', userId, 'items');
      
      // 해당 postId를 가진 알림 찾기
      const q = query(notificationsRef, where('postId', '==', postId));
      const notificationsSnapshot = await getDocs(q);
      
      // 찾은 알림들을 배치에 추가하여 삭제
      notificationsSnapshot.forEach((notifDoc) => {
        batch.delete(notifDoc.ref);
        deletedCount++;
      });
    }

    // 배치 실행
    if (deletedCount > 0) {
      await batch.commit();
    }

    return {
      success: true,
      message: `${deletedCount}개의 알림이 삭제되었습니다.`,
      deletedCount
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '알림 삭제 중 오류가 발생했습니다.',
      deletedCount: 0
    };
  }
};

/**
 * 게시물 접근 권한에 따른 대상 사용자 ID 목록 생성
 * @param {string} accessLevel - 접근 레벨 ('all', 'member', 'pro', 'admin', 'specific')
 * @param {array} specificUsers - 특정 사용자 ID 배열
 */
export const getNotificationTargetUsers = async (accessLevel, specificUsers = []) => {
  if (!db) {
    return { success: false, targetUsers: [] };
  }

  try {
    let targetUsers = [];

    if (accessLevel === 'specific') {
      // 특정 사용자만
      targetUsers = specificUsers.filter(id => id && id.trim());
    } else if (accessLevel === 'all') {
      // 전체 공개 - 모든 사용자에게 알림 (실제로는 로그인한 사용자만)
      // 너무 많을 수 있으므로 실제로는 최근 활동 사용자만 대상으로 할 수 있음
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      usersSnapshot.forEach((doc) => {
        targetUsers.push(doc.id);
      });
    } else if (accessLevel === 'member') {
      // 회원 전용 - 모든 회원
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      usersSnapshot.forEach((doc) => {
        targetUsers.push(doc.id);
      });
    } else if (accessLevel === 'pro') {
      // 프로 사용자 전용 (인덱스를 사용하여 효율적으로 조회)
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('isPro', '==', true));
      const usersSnapshot = await getDocs(q);
      usersSnapshot.forEach((doc) => {
        targetUsers.push(doc.id);
      });
    } else if (accessLevel === 'admin') {
      // 관리자 전용 (인덱스를 사용하여 효율적으로 조회)
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('isAdmin', '==', true));
      const usersSnapshot = await getDocs(q);
      usersSnapshot.forEach((doc) => {
        targetUsers.push(doc.id);
      });
    }

    return {
      success: true,
      targetUsers
    };

  } catch (error) {
    return {
      success: false,
      targetUsers: [],
      error: error.message
    };
  }
};

// ============================================
// 게시물 이미지 관리 API
// ============================================

/**
 * 게시물 이미지 업로드 (여러 개)
 * @param {string} postType - 게시물 종류 ('events', 'update_and_notice', 'FAQ')
 * @param {string} postId - 게시물 ID
 * @param {File[]} imageFiles - 업로드할 이미지 파일 배열
 * @returns {Promise<{success: boolean, downloadURLs?: string[], error?: string}>}
 */
export const uploadNoticePostImages = async (postType, postId, imageFiles) => {
  if (!storage) {
    return { success: false, error: 'Firebase Storage가 초기화되지 않았습니다.' };
  }

  if (!imageFiles || imageFiles.length === 0) {
    return { success: false, error: '업로드할 이미지가 없습니다.' };
  }

  try {
    const downloadURLs = [];
    const maxFileSize = 5 * 1024 * 1024; // 5MB 제한
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

    // 각 이미지 파일 업로드
    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i];

      // 파일 검증
      if (!allowedTypes.includes(file.type)) {
        continue;
      }

      if (file.size > maxFileSize) {
        continue;
      }

      // Storage 경로 설정
      const timestamp = Date.now();
      const fileName = `image_${timestamp}_${i}.${file.name.split('.').pop()}`;
      const imagePath = `notice_board_images/${postType}/${postId}/${fileName}`;
      const imageRef = ref(storage, imagePath);

      // 업로드
      const snapshot = await uploadBytes(imageRef, file, {
        contentType: file.type,
        customMetadata: {
          uploadedAt: new Date().toISOString(),
          postId: postId,
          postType: postType
        }
      });

      // 다운로드 URL 가져오기
      const downloadURL = await getDownloadURL(snapshot.ref);
      downloadURLs.push(downloadURL);
    }

    if (downloadURLs.length === 0) {
      return { success: false, error: '유효한 이미지가 없습니다.' };
    }

    return {
      success: true,
      downloadURLs: downloadURLs
    };

  } catch (error) {
    return {
      success: false,
      error: error.message || '이미지 업로드 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 게시물 이미지 삭제
 * @param {string[]} imageURLs - 삭제할 이미지 URL 배열
 */
export const deleteNoticePostImages = async (imageURLs) => {
  if (!storage || !imageURLs || imageURLs.length === 0) {
    return { success: true, message: '삭제할 이미지가 없습니다.' };
  }

  try {
    const deletePromises = imageURLs.map(async (url) => {
      try {
        // URL에서 Storage 경로 추출
        // URL 형식: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?...
        const urlObj = new URL(url);
        const pathMatch = urlObj.pathname.match(/\/o\/(.+)/);
        
        if (pathMatch) {
          const encodedPath = pathMatch[1].split('?')[0];
          const decodedPath = decodeURIComponent(encodedPath);
          const imageRef = ref(storage, decodedPath);
          
          await deleteObject(imageRef);
        }
      } catch (error) {
        // 이미 삭제되었거나 존재하지 않는 경우 무시
      }
    });

    await Promise.all(deletePromises);

    return { success: true, message: '이미지가 삭제되었습니다.' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export { db, auth, storage }; 